import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';

const STORAGE_KEY = 'floorplan-saved-clients';

/**
 * Generate unique ID for clients
 */
const generateId = () => Math.random().toString(36).substr(2, 9);

/**
 * Load saved clients from localStorage
 */
export const loadSavedClients = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load saved clients:', e);
  }
  return [];
};

/**
 * Save clients to localStorage
 */
export const saveClients = (clients) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(clients));
    return true;
  } catch (e) {
    console.error('Failed to save clients:', e);
    return false;
  }
};

/**
 * Add a new client
 */
export const addClient = (clientData) => {
  const clients = loadSavedClients();
  const newClient = {
    id: generateId(),
    ...clientData,
    createdAt: new Date().toISOString(),
  };
  clients.push(newClient);
  saveClients(clients);
  return newClient;
};

/**
 * Update an existing client
 */
export const updateClient = (clientId, clientData) => {
  const clients = loadSavedClients();
  const index = clients.findIndex(c => c.id === clientId);
  if (index >= 0) {
    clients[index] = { ...clients[index], ...clientData, updatedAt: new Date().toISOString() };
    saveClients(clients);
    return clients[index];
  }
  return null;
};

/**
 * Delete a client
 */
export const deleteClient = (clientId) => {
  const clients = loadSavedClients();
  const filtered = clients.filter(c => c.id !== clientId);
  saveClients(filtered);
  return filtered;
};

/**
 * Check if client with same name exists
 */
export const findClientByName = (name) => {
  if (!name || !name.trim()) return null;
  const clients = loadSavedClients();
  const normalizedName = name.trim().toLowerCase();
  return clients.find(c => c.name?.trim().toLowerCase() === normalizedName);
};

/**
 * ClientManager - Modal for managing saved clients
 */
export const ClientManager = ({ isOpen, onClose }) => {
  const [clients, setClients] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Form state for editing
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formAddress, setFormAddress] = useState('');

  // Load clients when modal opens
  useEffect(() => {
    if (isOpen) {
      setClients(loadSavedClients());
      setEditingClient(null);
      setConfirmDelete(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEdit = (client) => {
    setEditingClient(client.id);
    setFormName(client.name || '');
    setFormEmail(client.email || '');
    setFormPhone(client.phone || '');
    setFormAddress(client.address || '');
    setConfirmDelete(null);
  };

  const handleSaveEdit = () => {
    if (!formName.trim()) return;

    const updated = updateClient(editingClient, {
      name: formName.trim(),
      email: formEmail.trim(),
      phone: formPhone.trim(),
      address: formAddress.trim(),
    });

    if (updated) {
      setClients(loadSavedClients());
      setEditingClient(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingClient(null);
    setFormName('');
    setFormEmail('');
    setFormPhone('');
    setFormAddress('');
  };

  const handleDelete = (clientId) => {
    if (confirmDelete === clientId) {
      deleteClient(clientId);
      setClients(loadSavedClients());
      setConfirmDelete(null);
    } else {
      setConfirmDelete(clientId);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const handleAddNew = () => {
    const newClient = addClient({
      name: 'New Client',
      email: '',
      phone: '',
      address: '',
    });
    setClients(loadSavedClients());
    handleEdit(newClient);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={{ fontSize: '20px' }}>👥</span>
            <h2 style={styles.title}>Saved Clients</h2>
            <span style={styles.count}>({clients.length})</span>
          </div>
          <div style={styles.headerRight}>
            <Button variant="primary" size="small" onClick={handleAddNew}>
              + Add Client
            </Button>
            <Button variant="danger" size="small" onClick={onClose}>
              ✕
            </Button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {clients.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>👤</div>
              <div style={styles.emptyTitle}>No Saved Clients</div>
              <div style={styles.emptyText}>
                Clients will be saved automatically when you create estimates,
                or you can add them manually here.
              </div>
              <Button variant="primary" onClick={handleAddNew} style={{ marginTop: '20px', padding: '12px 24px' }}>
                + Add First Client
              </Button>
            </div>
          ) : (
            <div style={styles.list}>
              {clients.map((client) => (
                <div key={client.id} style={styles.clientCard}>
                  {editingClient === client.id ? (
                    // Edit mode
                    <div style={styles.editForm}>
                      <div style={styles.editField}>
                        <label style={styles.editLabel}>Name *</label>
                        <input
                          type="text"
                          value={formName}
                          onChange={(e) => setFormName(e.target.value)}
                          style={styles.editInput}
                          placeholder="Client name"
                          autoFocus
                        />
                      </div>
                      <div style={styles.editRow}>
                        <div style={styles.editField}>
                          <label style={styles.editLabel}>Email</label>
                          <input
                            type="email"
                            value={formEmail}
                            onChange={(e) => setFormEmail(e.target.value)}
                            style={styles.editInput}
                            placeholder="client@email.com"
                          />
                        </div>
                        <div style={styles.editField}>
                          <label style={styles.editLabel}>Phone</label>
                          <input
                            type="tel"
                            value={formPhone}
                            onChange={(e) => setFormPhone(e.target.value)}
                            style={styles.editInput}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                      <div style={styles.editField}>
                        <label style={styles.editLabel}>Address</label>
                        <textarea
                          value={formAddress}
                          onChange={(e) => setFormAddress(e.target.value)}
                          style={styles.editTextarea}
                          placeholder="123 Main Street&#10;City, State 12345"
                          rows={2}
                        />
                      </div>
                      <div style={styles.editActions}>
                        <Button variant="default" size="small" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleSaveEdit}
                          disabled={!formName.trim()}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <>
                      <div style={styles.clientInfo}>
                        <div style={styles.clientName}>{client.name || 'Unnamed Client'}</div>
                        <div style={styles.clientDetails}>
                          {client.email && (
                            <span style={styles.detailItem}>
                              <span style={styles.detailIcon}>✉</span>
                              {client.email}
                            </span>
                          )}
                          {client.phone && (
                            <span style={styles.detailItem}>
                              <span style={styles.detailIcon}>📞</span>
                              {client.phone}
                            </span>
                          )}
                          {client.address && (
                            <span style={styles.detailItem}>
                              <span style={styles.detailIcon}>📍</span>
                              {client.address.split('\n')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={styles.clientActions}>
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => handleEdit(client)}
                          title="Edit client"
                          style={{ padding: '6px 8px' }}
                        >
                          ✏️
                        </Button>
                        <Button
                          variant={confirmDelete === client.id ? 'danger' : 'ghost'}
                          size="small"
                          onClick={() => handleDelete(client.id)}
                          title={confirmDelete === client.id ? 'Click again to confirm' : 'Delete client'}
                          style={{ padding: '6px 8px' }}
                        >
                          {confirmDelete === client.id ? '⚠️' : '🗑️'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.footerInfo}>
            Clients are stored locally on this device
          </div>
          <Button variant="default" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#1a2332',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,200,255,0.05)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '600',
    color: '#00c8ff',
  },
  count: {
    color: '#6080a0',
    fontSize: '14px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  addButton: {
    background: '#00c8ff',
    border: 'none',
    color: '#000',
    padding: '8px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  closeButton: {
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    color: '#ff6666',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '4px',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  emptyTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  emptyText: {
    color: '#6080a0',
    fontSize: '14px',
    maxWidth: '300px',
    lineHeight: '1.5',
  },
  addButtonLarge: {
    marginTop: '20px',
    background: '#00c8ff',
    border: 'none',
    color: '#000',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  clientCard: {
    background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '8px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  clientInfo: {
    flex: 1,
    minWidth: 0,
  },
  clientName: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  clientDetails: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#6080a0',
    fontSize: '12px',
  },
  detailIcon: {
    fontSize: '10px',
  },
  clientActions: {
    display: 'flex',
    gap: '4px',
  },
  actionButton: {
    width: '32px',
    height: '32px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#6080a0',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteButton: {
    color: '#a06060',
  },
  deleteConfirm: {
    background: 'rgba(255,80,80,0.2)',
    borderColor: 'rgba(255,80,80,0.4)',
    color: '#ff6060',
  },
  // Edit form styles
  editForm: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  editRow: {
    display: 'flex',
    gap: '12px',
  },
  editField: {
    flex: 1,
  },
  editLabel: {
    display: 'block',
    fontSize: '10px',
    color: '#6080a0',
    marginBottom: '4px',
    textTransform: 'uppercase',
  },
  editInput: {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  editTextarea: {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'inherit',
  },
  editActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '4px',
  },
  cancelButton: {
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#6080a0',
    fontSize: '12px',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '6px 12px',
    background: '#00c8ff',
    border: 'none',
    borderRadius: '4px',
    color: '#000',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.2)',
  },
  footerInfo: {
    color: '#6080a0',
    fontSize: '11px',
  },
  doneButton: {
    padding: '8px 20px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '13px',
    cursor: 'pointer',
  },
};

export default ClientManager;
